import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, Search, Edit, Archive, ExternalLink, Info, Building2, User as UserIcon, Link2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  commercialista:"Commercialista", consulente:"Consulente", caf_patronato:"CAF/Patronato",
  banca:"Banca", confidi:"Confidi", mediazione_creditizia:"Mediazione creditizia",
  agenzia_immobiliare:"Agenzia immobiliare", segnalatore:"Segnalatore",
  professionista:"Professionista", associazione:"Associazione", ente:"Ente", altro:"Altro",
};
// Nota: "archiviato" non è più una scelta manuale nel menu Stato.
// Si imposta solo tramite l'azione "Archivia" nella scheda partner.
const STATUS_LABELS = { potenziale:"Potenziale", attivo:"Attivo", sospeso:"Sospeso", cessato:"Cessato" };
const STATUS_LABELS_ALL = { ...STATUS_LABELS, archiviato:"Archiviato" };

// ---------------- List ----------------
export function PartnersListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortBy, setSortBy] = useState("name");

  const load = async () => {
    const params = { limit: 300 };
    if (q) params.q = q;
    if (typeF) params.partner_type = typeF;
    if (statusF) params.status = statusF;
    if (includeArchived) params.include_archived = true;
    try {
      const r = await api.get("/partners", { params });
      setItems(r.data.items || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, typeF, statusF, includeArchived]);

  const sortedItems = useMemo(() => {
    const list = [...items];
    if (sortBy === "name") list.sort((a,b) => (a.name||"").localeCompare(b.name||"","it"));
    else if (sortBy === "last_contact_desc") list.sort((a,b) => (b.last_contact_at||"").localeCompare(a.last_contact_at||""));
    else if (sortBy === "next_action_asc") list.sort((a,b) => (a.next_action_at||"9999").localeCompare(b.next_action_at||"9999"));
    else if (sortBy === "created_desc") list.sort((a,b) => (b.created_at||"").localeCompare(a.created_at||""));
    return list;
  }, [items, sortBy]);

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="partners-title">Partner</h1>
        <div className="ma-quick-actions">
          <Link to="/partners/new" className="ma-btn-primary-sm" data-testid="btn-new-partner"><Plus size={16}/> Nuovo partner</Link>
        </div>
      </div>
      <div className="ma-filters">
        <div className="ma-search"><Search size={14}/><input placeholder="Cerca per nome, CF, PIVA, email…" value={q} onChange={e=>setQ(e.target.value)} data-testid="partner-search"/></div>
        <select className="ma-input-sm" value={typeF} onChange={e=>setTypeF(e.target.value)} data-testid="partner-type-filter">
          <option value="">Tutte le tipologie</option>
          {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ma-input-sm" value={statusF} onChange={e=>setStatusF(e.target.value)} data-testid="partner-status-filter">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_LABELS_ALL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ma-input-sm" value={sortBy} onChange={e=>setSortBy(e.target.value)} data-testid="partner-sort">
          <option value="name">Ordina: Nome</option>
          <option value="last_contact_desc">Ultimo contatto (recente)</option>
          <option value="next_action_asc">Prossima azione (prima)</option>
          <option value="created_desc">Creazione (recente)</option>
        </select>
        <label className="ma-checkbox" style={{marginLeft:8}}>
          <input type="checkbox" checked={includeArchived} onChange={e=>setIncludeArchived(e.target.checked)}/> Includi archiviati
        </label>
        <div className="ma-total">Totale: {items.length}</div>
      </div>

      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead><tr><th>Codice</th><th>Nome</th><th>Tipologia</th><th>Referente</th><th>Email</th><th>Territorio</th><th>Ultimo contatto</th><th>Stato</th></tr></thead>
          <tbody>
            {sortedItems.map(p => (
              <tr key={p.id} data-testid={`partner-row-${p.id}`}>
                <td><span className="ma-code-badge">{p.code || "—"}</span></td>
                <td><Link to={`/partners/${p.id}`} className="ma-link">{p.name}</Link></td>
                <td>{TYPE_LABELS[p.partner_type] || p.partner_type}</td>
                <td>{p.contact_person || "—"}</td>
                <td>{p.email || "—"}</td>
                <td>{p.territory || "—"}</td>
                <td>{p.last_contact_at ? new Date(p.last_contact_at).toLocaleDateString("it-IT") : "—"}</td>
                <td><span className={`ma-pill ${p.deleted_at ? "pill-gray" : p.status==="attivo" ? "pill-ok" : p.status==="sospeso" ? "pill-warn" : "pill-gray"}`}>
                  {p.deleted_at ? "Archiviato" : (STATUS_LABELS_ALL[p.status] || p.status)}</span></td>
              </tr>
            ))}
            {sortedItems.length === 0 && <tr><td colSpan={8} className="ma-empty">Nessun partner presente.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Form (Create + Edit) ----------------
const AUTOFILL_FIELDS = ["name","fiscal_code","vat_number","email","pec","phone","website","address"];

function toDatetimeLocal(iso) {
  if (!iso) return "";
  // "2026-03-15T10:00:00Z" -> "2026-03-15T10:00"
  return iso.slice(0, 16);
}

export function PartnerFormPage({ edit=false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const emptyForm = {
    name:"", partner_type:"commercialista", status:"attivo", contact_person:"", email:"", pec:"",
    phone:"", vat_number:"", fiscal_code:"", website:"", address:"", territory:"",
    collaboration_start_date:"", acquisition_channel:"",
    responsible_user_id:"", linked_subject_id:"",
    last_contact_at:"", next_action_at:"", next_action_note:"",
    privacy_consent:false, privacy_consent_date:"",
    notes:"", is_active:true,
  };
  const [f, setF] = useState(emptyForm);
  const [subjects, setSubjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [linkedSubject, setLinkedSubject] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load lookups
  useEffect(() => {
    api.get("/subjects", { params: { limit: 200 } })
      .then(r => setSubjects(r.data.items || []))
      .catch(() => setSubjects([]));
    api.get("/users")
      .then(r => setUsers((Array.isArray(r.data) ? r.data : []).filter(u => u.is_active)))
      .catch(() => setUsers([]));
    api.get("/admin/config/acquisition_channels")
      .then(r => setChannels((Array.isArray(r.data) ? r.data : []).filter(c => c.is_active)))
      .catch(() => setChannels([]));
  }, []);

  // Load partner (edit mode)
  useEffect(() => {
    if (!edit || !id) return;
    api.get(`/partners/${id}`).then(r => {
      const d = r.data;
      setF({
        ...emptyForm, ...d,
        linked_subject_id: d.linked_subject_id || "",
        responsible_user_id: d.responsible_user_id || "",
        acquisition_channel: d.acquisition_channel || "",
        collaboration_start_date: d.collaboration_start_date || "",
        privacy_consent_date: d.privacy_consent_date || "",
        last_contact_at: toDatetimeLocal(d.last_contact_at),
        next_action_at: toDatetimeLocal(d.next_action_at),
        next_action_note: d.next_action_note || "",
      });
    }).catch(() => {});
  // eslint-disable-next-line
  }, [edit, id]);

  // Quando linked_subject_id cambia → carica il soggetto e sincronizza i campi anagrafici
  useEffect(() => {
    if (!f.linked_subject_id) { setLinkedSubject(null); return; }
    let cancel = false;
    api.get(`/subjects/${f.linked_subject_id}`).then(r => {
      if (cancel) return;
      const s = r.data;
      setLinkedSubject(s);
      // Autofill: la fonte di verità sono i dati dell'anagrafica
      const addr0 = (s.addresses && s.addresses[0]) || null;
      const composedAddress = addr0
        ? [addr0.street_address, addr0.city, addr0.province, addr0.postal_code].filter(Boolean).join(", ")
        : "";
      setF(prev => ({
        ...prev,
        name: s.display_name || prev.name,
        fiscal_code: s.fiscal_code || "",
        vat_number: s.vat_number || "",
        email: s.email || "",
        pec: s.pec || "",
        phone: s.phone || "",
        website: s.website || "",
        address: composedAddress || prev.address,
      }));
    }).catch(() => setLinkedSubject(null));
    return () => { cancel = true; };
  // eslint-disable-next-line
  }, [f.linked_subject_id]);

  const clearLink = () => {
    setF(prev => ({ ...prev, linked_subject_id: "" }));
    setLinkedSubject(null);
    // I dati anagrafici restano come sono (l'utente può ora modificarli manualmente)
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f };
      p.linked_subject_id = f.linked_subject_id || null;
      p.responsible_user_id = f.responsible_user_id || null;
      p.acquisition_channel = f.acquisition_channel || null;
      if (!p.collaboration_start_date) p.collaboration_start_date = null;
      if (!p.privacy_consent_date) p.privacy_consent_date = null;
      p.last_contact_at = f.last_contact_at ? new Date(f.last_contact_at).toISOString() : null;
      p.next_action_at = f.next_action_at ? new Date(f.next_action_at).toISOString() : null;
      let res;
      if (edit && id) res = await api.patch(`/partners/${id}`, p);
      else res = await api.post("/partners", p);
      toast.success(edit ? "Partner aggiornato" : `Partner creato (${res.data.code})`);
      nav(`/partners/${res.data.id}`);
    } catch { setBusy(false); }
  };

  const isLinked = !!f.linked_subject_id;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/partners">Partner</Link> · {edit ? "Modifica" : "Nuovo"}</div>
          <h1 className="ma-h1">{edit ? "Modifica partner" : "Nuovo partner"}</h1>
        </div>
      </div>
      <form onSubmit={submit} className="ma-form-panel">
        <div className="ma-form-grid">

          {/* --- Collegamento a soggetto (persona/impresa) --- */}
          <div className="ma-col-full">
            <label className="ma-label" htmlFor="pf-linked">Persona/Impresa collegata</label>
            <select id="pf-linked" data-testid="pf-linked-subject" className="ma-input"
                    value={f.linked_subject_id||""}
                    onChange={e=>setF({...f, linked_subject_id:e.target.value})}>
              <option value="">— Nessuna (partner con anagrafica autonoma) —</option>
              {subjects.map(s => <option key={s.id} value={s.id}>
                {s.display_name} — {s.subject_type === "person" ? "Persona" : "Impresa"}
              </option>)}
            </select>
            {isLinked && linkedSubject && (
              <div className="ma-notice" style={{marginTop:8, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}} data-testid="pf-linked-notice">
                <Link2 size={14}/>
                <span>
                  Partner collegato a <b>{linkedSubject.display_name}</b> ({linkedSubject.subject_type === "person" ? "persona" : "impresa"}).
                  I dati anagrafici (nome, CF, PIVA, email, PEC, telefono, sito, indirizzo) vengono <b>ereditati dall'anagrafica</b> e sono in sola lettura qui — modificali dalla scheda cliente per evitare duplicazioni.
                </span>
                <Link className="ma-link" to={`/clients/${linkedSubject.id}`} data-testid="pf-linked-open">
                  <ExternalLink size={12}/> Apri anagrafica
                </Link>
                <button type="button" className="ma-btn-link" onClick={clearLink} data-testid="pf-linked-clear">Scollega</button>
              </div>
            )}
            {!isLinked && (
              <div className="ma-sub-xs" style={{marginTop:6}}>
                <Info size={11}/> Se in futuro colleghi questo partner a un soggetto esistente, i dati anagrafici verranno <b>sostituiti</b> con quelli dell'anagrafica selezionata (i campi liberi come Referente, Territorio, Note restano invariati).
              </div>
            )}
          </div>

          {/* --- Sezione principale --- */}
          <div><label className="ma-label">Nome / Denominazione *</label>
            <input className="ma-input" required value={f.name}
                    onChange={e=>setF({...f, name:e.target.value})}
                    readOnly={isLinked}
                    disabled={isLinked}
                    data-testid="pf-name"/></div>
          <div><label className="ma-label">Tipologia *</label>
            <select className="ma-input" value={f.partner_type} onChange={e=>setF({...f, partner_type:e.target.value})} data-testid="pf-type">
              {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="ma-label">Referente principale</label>
            <input className="ma-input" value={f.contact_person||""} onChange={e=>setF({...f, contact_person:e.target.value})} data-testid="pf-contact-person"/></div>
          <div><label className="ma-label">Stato</label>
            <select className="ma-input" value={f.status === "archiviato" ? "attivo" : f.status}
                    onChange={e=>setF({...f, status:e.target.value})} data-testid="pf-status">
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>

          {/* --- Contatti (autofill se collegato) --- */}
          <div><label className="ma-label">Codice fiscale</label>
            <input className="ma-input" value={f.fiscal_code||""} onChange={e=>setF({...f, fiscal_code:e.target.value.toUpperCase()})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div><label className="ma-label">Partita IVA</label>
            <input className="ma-input" value={f.vat_number||""} onChange={e=>setF({...f, vat_number:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div><label className="ma-label">Email</label>
            <input className="ma-input" type="email" value={f.email||""} onChange={e=>setF({...f, email:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div><label className="ma-label">PEC</label>
            <input className="ma-input" type="email" value={f.pec||""} onChange={e=>setF({...f, pec:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div><label className="ma-label">Telefono</label>
            <input className="ma-input" value={f.phone||""} onChange={e=>setF({...f, phone:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div><label className="ma-label">Sito web</label>
            <input className="ma-input" value={f.website||""} onChange={e=>setF({...f, website:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>
          <div className="ma-col-full"><label className="ma-label">Indirizzo</label>
            <input className="ma-input" value={f.address||""} onChange={e=>setF({...f, address:e.target.value})}
                   readOnly={isLinked} disabled={isLinked}/></div>

          {/* --- Sezione collaborazione --- */}
          <div><label className="ma-label">Territorio/Area operativa</label>
            <input className="ma-input" value={f.territory||""} onChange={e=>setF({...f, territory:e.target.value})} data-testid="pf-territory"/></div>
          <div><label className="ma-label">Data inizio collaborazione</label>
            <input type="date" className="ma-input" value={f.collaboration_start_date||""} onChange={e=>setF({...f, collaboration_start_date:e.target.value})}/></div>
          <div><label className="ma-label">Canale acquisizione</label>
            <select className="ma-input" value={f.acquisition_channel||""} onChange={e=>setF({...f, acquisition_channel:e.target.value})} data-testid="pf-acq-channel">
              <option value="">— Nessuno —</option>
              {channels.length === 0
                ? <option value="" disabled>Nessun canale configurato</option>
                : channels.map(c => <option key={c.id} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div><label className="ma-label">Responsabile interno</label>
            <select className="ma-input" value={f.responsible_user_id||""} onChange={e=>setF({...f, responsible_user_id:e.target.value})} data-testid="pf-responsible">
              <option value="">— Nessuno —</option>
              {users.length === 0
                ? <option value="" disabled>Nessun utente attivo</option>
                : users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
            </select>
          </div>

          {/* --- Attività --- */}
          <div><label className="ma-label">Data ultimo contatto</label>
            <input type="datetime-local" className="ma-input"
                   value={f.last_contact_at||""} onChange={e=>setF({...f, last_contact_at:e.target.value})}
                   data-testid="pf-last-contact"/></div>
          <div><label className="ma-label">Data e ora prossima azione</label>
            <input type="datetime-local" className="ma-input"
                   value={f.next_action_at||""} onChange={e=>setF({...f, next_action_at:e.target.value})}
                   data-testid="pf-next-action-at"/></div>
          <div className="ma-col-full"><label className="ma-label">Descrizione prossima azione</label>
            <input className="ma-input" value={f.next_action_note||""}
                   onChange={e=>setF({...f, next_action_note:e.target.value})}
                   placeholder="Es. Richiamare per aggiornamento pratica"
                   data-testid="pf-next-action-note"/></div>

          {/* --- Privacy --- */}
          <div className="ma-col-full">
            <label className="ma-checkbox"><input type="checkbox" checked={f.privacy_consent} onChange={e=>setF({...f, privacy_consent:e.target.checked})}/> Consenso privacy raccolto</label>
          </div>
          {f.privacy_consent && (
            <div><label className="ma-label">Data consenso</label>
              <input type="date" className="ma-input" value={f.privacy_consent_date||""} onChange={e=>setF({...f, privacy_consent_date:e.target.value})}/></div>
          )}

          {/* --- Note --- */}
          <div className="ma-col-full"><label className="ma-label">Note</label>
            <textarea className="ma-input" rows={3} value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/></div>
        </div>
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={()=>nav(-1)}>Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="pf-submit">
            {busy ? "Salvataggio…" : (edit ? "Salva modifiche" : "Crea partner")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Detail ----------------
export function PartnerDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("info");
  const [userMap, setUserMap] = useState({});
  const [channelMap, setChannelMap] = useState({});

  const load = () => api.get(`/partners/${id}`).then(r => setP(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/users").then(r => {
      const m = {}; (Array.isArray(r.data) ? r.data : []).forEach(u => { m[u.id] = `${u.first_name} ${u.last_name}`; });
      setUserMap(m);
    }).catch(() => {});
    api.get("/admin/config/acquisition_channels").then(r => {
      const m = {}; (Array.isArray(r.data) ? r.data : []).forEach(c => { m[c.code] = c.label; });
      setChannelMap(m);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const doArchive = async () => {
    if (!window.confirm("Archiviare questo partner? I collegamenti resteranno visibili in sola lettura.")) return;
    try { await api.post(`/partners/${id}/archive`); toast.success("Partner archiviato"); load(); } catch {}
  };
  const doRestore = async () => {
    try { await api.post(`/partners/${id}/restore`); toast.success("Partner ripristinato"); load(); } catch {}
  };

  if (!p) return <div className="ma-loading">Caricamento…</div>;
  const isLinked = !!p.linked_subject_id;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/partners">Partner</Link></div>
          <h1 className="ma-h1" data-testid="partner-detail-title"><span className="ma-code-badge">{p.code}</span> {p.name}</h1>
          <div className="ma-sub">
            <span className="ma-pill">{TYPE_LABELS[p.partner_type] || p.partner_type}</span>
            <span className={`ma-pill ${p.deleted_at ? "pill-gray" : p.status==="attivo"?"pill-ok":"pill-warn"}`}>
              {p.deleted_at ? "Archiviato" : (STATUS_LABELS_ALL[p.status] || p.status)}</span>
            {p.territory && <span className="ma-pill pill-gray">{p.territory}</span>}
            {isLinked && <span className="ma-pill pill-blue"><Link2 size={11} style={{verticalAlign:"middle"}}/> Collegato ad anagrafica</span>}
          </div>
        </div>
        <div className="ma-quick-actions">
          {!p.deleted_at && <Link to={`/partners/${id}/edit`} className="ma-btn-secondary" data-testid="pd-edit"><Edit size={14}/> Modifica</Link>}
          {!p.deleted_at
            ? <button className="ma-btn-secondary" onClick={doArchive} data-testid="pd-archive"><Archive size={14}/> Archivia</button>
            : <button className="ma-btn-primary-sm" onClick={doRestore} data-testid="pd-restore">Ripristina</button>}
        </div>
      </div>

      <div className="ma-tabs">
        <button className={`ma-tab ${tab==="info"?"active":""}`} onClick={()=>setTab("info")}>Informazioni</button>
        <button className={`ma-tab ${tab==="clients"?"active":""}`} onClick={()=>setTab("clients")}>Clienti segnalati ({p.referred_clients_count})</button>
        <button className={`ma-tab ${tab==="practices"?"active":""}`} onClick={()=>setTab("practices")}>Pratiche collegate ({p.practices_count})</button>
      </div>

      {tab === "info" && (
        <div className="ma-info-grid">
          <div className="ma-info-block">
            <h3 className="ma-h3">Contatti</h3>
            <div className="ma-row-kv"><div className="ma-row-k">Referente</div><div className="ma-row-v">{p.contact_person || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Email</div><div className="ma-row-v">{p.email || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">PEC</div><div className="ma-row-v">{p.pec || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Telefono</div><div className="ma-row-v">{p.phone || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Sito</div><div className="ma-row-v">
              {p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="ma-link"><ExternalLink size={12}/> {p.website}</a> : "—"}
            </div></div>
          </div>
          <div className="ma-info-block">
            <h3 className="ma-h3">Anagrafica</h3>
            <div className="ma-row-kv"><div className="ma-row-k">Codice fiscale</div><div className="ma-row-v ma-mono">{p.fiscal_code || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Partita IVA</div><div className="ma-row-v ma-mono">{p.vat_number || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Indirizzo</div><div className="ma-row-v">{p.address || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Territorio</div><div className="ma-row-v">{p.territory || "—"}</div></div>
            {isLinked && <div className="ma-row-kv"><div className="ma-row-k">Anagrafica collegata</div>
              <div className="ma-row-v"><Link className="ma-link" to={`/clients/${p.linked_subject_id}`} data-testid="pd-linked-open"><Building2 size={12}/> Apri scheda</Link></div></div>}
          </div>
          <div className="ma-info-block">
            <h3 className="ma-h3">Attività</h3>
            <div className="ma-row-kv"><div className="ma-row-k">Responsabile interno</div>
              <div className="ma-row-v" data-testid="pd-responsible">{p.responsible_user_id ? (userMap[p.responsible_user_id] || "—") : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Inizio collaborazione</div><div className="ma-row-v">{p.collaboration_start_date || "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Canale acquisizione</div>
              <div className="ma-row-v" data-testid="pd-acq-channel">{p.acquisition_channel ? (channelMap[p.acquisition_channel] || p.acquisition_channel) : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Data ultimo contatto</div>
              <div className="ma-row-v" data-testid="pd-last-contact">{p.last_contact_at ? new Date(p.last_contact_at).toLocaleString("it-IT") : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Prossima azione</div>
              <div className="ma-row-v" data-testid="pd-next-action">
                {p.next_action_at ? <b>{new Date(p.next_action_at).toLocaleString("it-IT")}</b> : null}
                {p.next_action_at && p.next_action_note && " · "}
                {p.next_action_note || (!p.next_action_at ? "—" : "")}
              </div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Consenso privacy</div>
              <div className="ma-row-v">{p.privacy_consent ? `Sì (${p.privacy_consent_date || "data non indicata"})` : "No"}</div></div>
          </div>
          {p.notes && <div className="ma-info-block ma-col-full">
            <h3 className="ma-h3">Note</h3>
            <div className="ma-notes">{p.notes}</div>
          </div>}
        </div>
      )}

      {tab === "clients" && (
        <div className="ma-table-wrap"><table className="ma-table">
          <thead><tr><th>Tipo</th><th>Denominazione</th></tr></thead>
          <tbody>
            {(p.referred_clients||[]).map(c => (
              <tr key={c.id}><td>{c.subject_type==="person"?"Persona":"Impresa"}</td>
                <td><Link to={`/clients/${c.id}`} className="ma-link">{c.display_name}</Link></td></tr>
            ))}
            {(p.referred_clients||[]).length===0 && <tr><td colSpan={2} className="ma-empty">Nessun cliente segnalato.</td></tr>}
          </tbody>
        </table></div>
      )}
      {tab === "practices" && (
        <div className="ma-table-wrap"><table className="ma-table">
          <thead><tr><th>Codice</th><th>Titolo</th><th>Ruolo</th><th>Note</th></tr></thead>
          <tbody>
            {(p.practices||[]).map(pr => (
              <tr key={pr.id}><td><Link to={`/practices/${pr.id}`} className="ma-link">{pr.code}</Link></td>
                <td>{pr.title}</td><td>{pr.role||"—"}</td><td className="ma-truncate">{pr.notes||"—"}</td></tr>
            ))}
            {(p.practices||[]).length===0 && <tr><td colSpan={4} className="ma-empty">Nessuna pratica collegata.</td></tr>}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
